import axios from 'axios';
import { api } from './api';

// Instance d'API spécifique pour Discord
const discordApi = axios.create({
    baseURL: 'https://discord.com/api/v10',
    headers: {
        'Content-Type': 'application/json'
    }
});

// Instance d'API spécifique pour Twitch
const twitchApi = axios.create({
    baseURL: 'https://api.twitch.tv/helix',
    headers: {
        'Content-Type': 'application/json'
    }
});

// Ajouter l'intercepteur pour le token Discord
discordApi.interceptors.request.use(
    config => {
        const token = localStorage.getItem('discord_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    error => Promise.reject(error)
);

// Ajouter l'intercepteur pour le token Twitch
twitchApi.interceptors.request.use(
    config => {
        const token = localStorage.getItem('twitch_access_token');
        const clientId = process.env.VUE_APP_TWITCH_CLIENT_ID;
        
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        
        if (clientId) {
            config.headers['Client-ID'] = clientId;
        }
        
        return config;
    },
    error => Promise.reject(error)
);

export const authService = {
    async getUserInfo() {
        try {
            //console.log('Fetching Discord user info...');
            //console.log('Discord token exists:', !!localStorage.getItem('discord_token'));
            
            // Utiliser l'instance Discord API
            const response = await discordApi.get('/users/@me');
            //console.log('Discord API response:', response.data);
            
            if (response && response.data) {
                // Stocker les données utilisateur dans le localStorage
                localStorage.setItem('discord_user', JSON.stringify(response.data));
                return response.data;
            }
            throw new Error('Invalid response from Discord API');
        } catch (error) {
            console.error('Error fetching user info:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            
            // Si on a des données en cache, on les utilise
            const cachedUser = localStorage.getItem('discord_user');
            //console.log('Using cached user data:', !!cachedUser);
            if (cachedUser) {
                return JSON.parse(cachedUser);
            }
            throw error;
        }
    },

    // Note: L'échange du code contre un token doit être effectué côté serveur pour des raisons de sécurité
    // Le client secret ne doit jamais être exposé côté client
    // Cette méthode a été supprimée et remplacée par une approche plus sécurisée où le backend gère l'échange
    
    // Cette méthode est désormais une méthode de commodité pour récupérer les données utilisateur Twitch du localStorage
    // Les données complètes de l'utilisateur seront récupérées par le backend lors de la création du compte
    getUserData() {
        try {
            // Récupérer les données utilisateur Discord du localStorage
            const cachedUser = localStorage.getItem('discord_user');
            
            if (cachedUser) {
                const userData = JSON.parse(cachedUser);
                // Construire l'URL de l'avatar Discord si disponible
                if (userData.id && userData.avatar) {
                    userData.discordAvatarUrl = `https://cdn.discordapp.com/avatars/${userData.id}/${userData.avatar}.png`;
                }
                return userData;
            }
            
            return null;
        } catch (error) {
            console.error('Error getting user data from localStorage:', error);
            return null;
        }
    },

    getTwitchUserInfo() {
        try {
            //console.log('Getting Twitch user info from localStorage...');
            
            // Vérifier si nous avons des données en cache
            const cachedUser = localStorage.getItem('twitch_user');
            //console.log('Cached Twitch user data exists:', !!cachedUser);
            
            if (cachedUser) {
                return JSON.parse(cachedUser);
            }
            
            // Si nous n'avons pas de données en cache, retourner un objet vide
            // Le backend se chargera de récupérer les informations complètes
            return {};
        } catch (error) {
            console.error('Error getting Twitch user info from localStorage:', error);
            return {};
        }
    },

    async getSubscriptionInfo(options = {}) {
        const { useCache = true } = options;
        try {
            const response = await api.get('/subscriptions');
            if (response && response.data) {
                localStorage.setItem('subscription_info', JSON.stringify(response.data));
                return response.data;
            }
            throw new Error('Invalid response from subscription API');
        } catch (error) {
            console.error('Error fetching subscription info:', error);
            if (useCache) {
                const cachedInfo = localStorage.getItem('subscription_info');
                if (cachedInfo) {
                    return JSON.parse(cachedInfo);
                }
            }
            throw error;
        }
    },

    async logout() {
        try {
            // Appeler l'API pour invalider le token
            await api.post('/subscriptions/logout');
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            // Clear all stored data
            localStorage.removeItem('discord_token');
            localStorage.removeItem('discord_user');
            localStorage.removeItem('twitch_access_token');
            localStorage.removeItem('twitch_refresh_token');
            localStorage.removeItem('twitch_user');
            localStorage.removeItem('subscription_info');
            localStorage.removeItem('impersonated_user');
            
            // Redirect to home page
            window.location.href = '/';
        }
    }
};
